/**
 * @fileoverview Month grid and label helpers for the home calendar.
 * @module components/HomeCalendar/homeCalendarDates
 */

import {
  DAYS_PER_WEEK,
  FIRST_MONTH_INDEX,
  LAST_MONTH_INDEX,
  SUNDAY_REFERENCE_DAY,
  SUNDAY_REFERENCE_MONTH,
  SUNDAY_REFERENCE_YEAR,
} from "./homeCalendarConstants";

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const isSameMonthAndYear = (left, right) =>
  left.getMonth() === right.getMonth() &&
  left.getFullYear() === right.getFullYear();

/**
 * Localized month name with the first letter capitalized.
 * @param {Date} date
 * @param {string} language
 * @returns {string}
 */
export const formatMonthName = (date, language) => {
  const monthName = date.toLocaleDateString(language, { month: "long" });
  return capitalize(monthName);
};

/**
 * Sunday-to-Saturday weekday labels for the current language.
 * January 7, 2024 is a Sunday, so each following day is the next weekday.
 * @param {string} language
 * @returns {string[]}
 */
export const buildDayNames = (language) => {
  const names = [];

  for (let i = 0; i < DAYS_PER_WEEK; i++) {
    const date = new Date(
      SUNDAY_REFERENCE_YEAR,
      SUNDAY_REFERENCE_MONTH,
      SUNDAY_REFERENCE_DAY + i,
    );
    const dayName = date.toLocaleDateString(language, { weekday: "long" });
    names.push(capitalize(dayName));
  }

  return names;
};

/**
 * @param {Date} currentDate
 * @param {Date} [today]
 * @returns {boolean}
 */
export const isViewingCurrentMonth = (currentDate, today = new Date()) =>
  isSameMonthAndYear(currentDate, today);

/**
 * @param {Date} currentDate
 * @param {Date} [today]
 * @returns {boolean}
 */
export const isViewingNextMonth = (currentDate, today = new Date()) => {
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  return isSameMonthAndYear(currentDate, nextMonth);
};

const pushMonthDay = (days, date, isCurrentMonth, isToday) => {
  days.push({
    date,
    dayNumber: date.getDate(),
    isCurrentMonth,
    isToday,
  });
};

const appendLeadingDays = (days, year, month) => {
  const firstDay = new Date(year, month, 1);
  const daysFromPreviousMonth = firstDay.getDay();
  const previousMonth =
    month === FIRST_MONTH_INDEX ? LAST_MONTH_INDEX : month - 1;
  const previousMonthYear = month === FIRST_MONTH_INDEX ? year - 1 : year;
  const daysInPreviousMonth = new Date(
    previousMonthYear,
    previousMonth + 1,
    0,
  ).getDate();

  for (let i = daysFromPreviousMonth - 1; i >= 0; i--) {
    pushMonthDay(
      days,
      new Date(previousMonthYear, previousMonth, daysInPreviousMonth - i),
      false,
      false,
    );
  }
};

const appendCurrentMonthDays = (days, year, month, lastDayNumber) => {
  const today = new Date();

  for (let day = 1; day <= lastDayNumber; day++) {
    const date = new Date(year, month, day);
    pushMonthDay(
      days,
      date,
      true,
      date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear(),
    );
  }
};

const appendTrailingDays = (days, year, month, lastDayOfWeek) => {
  const daysFromNextMonth = DAYS_PER_WEEK - 1 - lastDayOfWeek;
  const nextMonth = month === LAST_MONTH_INDEX ? FIRST_MONTH_INDEX : month + 1;
  const nextMonthYear = month === LAST_MONTH_INDEX ? year + 1 : year;

  for (let day = 1; day <= daysFromNextMonth; day++) {
    pushMonthDay(days, new Date(nextMonthYear, nextMonth, day), false, false);
  }
};

/**
 * Builds the weeks shown for a month, including leading and trailing days
 * so every row has a full week.
 * @param {Date} currentDate
 * @returns {Array<Array<Object>>}
 */
export const buildMonthWeeks = (currentDate) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  appendLeadingDays(days, year, month);
  appendCurrentMonthDays(days, year, month, lastDay.getDate());
  appendTrailingDays(days, year, month, lastDay.getDay());

  const weeks = [];
  for (let i = 0; i < days.length; i += DAYS_PER_WEEK) {
    weeks.push(days.slice(i, i + DAYS_PER_WEEK));
  }

  return weeks;
};
